import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { toast } from 'react-toastify';
import { Link, useLocation } from 'react-router-dom';
import { FaTrash, FaEye } from 'react-icons/fa';

const Orders = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [orders, setOrders] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchOrders = async () => {
        try {
            const res = await axios.get('/orders');
            setOrders(res.data);
        } catch (error) {
            console.error('Siparişler yüklenemedi');
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    useEffect(() => {
        if (location.state?.status) {
            setStatusFilter(location.state.status);
        }
    }, [location.state]);

    const handleStatusChange = async (id, newStatus) => {
        // Anlık olarak arayüzü güncelle (Optimistic Update)
        setOrders(prevOrders => prevOrders.map(order => order.id === id ? { ...order, status: newStatus } : order));

        try {
            await axios.put(`/orders/${id}/status`, { status: newStatus });
            toast.success('Sipariş durumu güncellendi.');
        } catch (error) {
            console.error("Güncelleme hatası:", error);
            toast.error('Güncelleme başarısız.');
            fetchOrders(); // Hata olursa eski haline döndür
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bu siparişi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
            try {
                await axios.delete(`/orders/${id}`);
                toast.success('Sipariş silindi.');
                setOrders(prev => prev.filter(o => o.id !== id));
            } catch (error) {
                toast.error('Silme işlemi başarısız.');
            }
        }
    };

    // Statüye göre renk belirleme
    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending': return <span className="badge bg-warning text-dark">{t('my_orders.status_pending')}</span>;
            case 'approved': return <span className="badge bg-info text-dark">{t('my_orders.status_approved')}</span>;
            case 'preparing': return <span className="badge bg-primary">{t('my_orders.status_preparing')}</span>;
            case 'shipped': return <span className="badge bg-secondary">{t('my_orders.status_shipped')}</span>;
            case 'delivered': return <span className="badge bg-success">{t('my_orders.status_delivered')}</span>;
            case 'cancelled': return <span className="badge bg-danger">{t('my_orders.status_cancelled')}</span>;
            default: return <span className="badge bg-light text-dark">{status}</span>;
        }
    };

    // Filtreleme Mantığı
    const filteredOrders = orders.filter(order => {
        const orderDate = new Date(order.created_at);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        
        // Bitiş tarihini gün sonuna ayarla
        if (end) end.setHours(23, 59, 59, 999);

        const matchesStatus = statusFilter ? order.status === statusFilter : true;
        const matchesSearch = searchTerm ? 
            (order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) || 
             (order.company_name || order.username).toLowerCase().includes(searchTerm.toLowerCase())) 
            : true;
        const matchesDate = (!start || orderDate >= start) && (!end || orderDate <= end);

        return matchesStatus && matchesSearch && matchesDate;
    });

    // Durum Filtre Butonları
    const statusButtons = [
        { label: t('admin_orders.all'), value: '', color: 'secondary' },
        { label: t('my_orders.status_pending'), value: 'pending', color: 'warning' },
        { label: t('my_orders.status_approved'), value: 'approved', color: 'info' },
        { label: t('my_orders.status_preparing'), value: 'preparing', color: 'primary' },
        { label: t('my_orders.status_shipped'), value: 'shipped', color: 'secondary' },
        { label: t('my_orders.status_delivered'), value: 'delivered', color: 'success' },
        { label: t('my_orders.status_cancelled'), value: 'cancelled', color: 'danger' }
    ];

    return (
        <AdminLayout>
            <h2>{t('admin_orders.title')}</h2>
            
            {/* Hızlı Durum Filtreleri */}
            <div className="d-flex flex-wrap gap-2 mt-3">
                {statusButtons.map((btn) => (
                    <button
                        key={btn.value}
                        className={`btn ${statusFilter === btn.value ? `btn-${btn.color}` : 'btn-outline-secondary'}`}
                        onClick={() => setStatusFilter(btn.value)}
                    >
                        {btn.label}
                    </button>
                ))}
            </div>

            {/* Filtreleme Alanı */}
            <div className="card shadow-sm border-0 mt-3 mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <input 
                                type="text" 
                                className="form-control" 
                                placeholder={t('admin_orders.search_placeholder')} 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <div className="input-group">
                                <span className="input-group-text bg-light">{t('my_orders.start_date')}</span>
                                <input 
                                    type="date" 
                                    className="form-control" 
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="input-group">
                                <span className="input-group-text bg-light">{t('my_orders.end_date')}</span>
                                <input 
                                    type="date" 
                                    className="form-control" 
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <button 
                                className="btn btn-outline-secondary w-100"
                                onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('');
                                    setStartDate('');
                                    setEndDate('');
                                }}
                                title="Filtreleri Temizle"
                            >
                                Temizle
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm border-0">
                {/* Desktop: Table View */}
                <div className="table-responsive d-none d-lg-block">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>{t('admin_orders.order_no')}</th>
                                <th>{t('admin_orders.dealer_firm')}</th>
                                <th>{t('admin_orders.amount')}</th>
                                <th>{t('admin_orders.date')}</th>
                                <th>{t('admin_orders.status')}</th>
                                <th>{t('admin_orders.action')}</th>
                                <th>{t('admin_orders.detail')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map(order => (
                                <tr key={order.id} className="transition-hover">
                                    <td><span className="fw-bold text-primary">{order.order_number}</span></td>
                                    <td>
                                        <div className="fw-bold text-dark">{order.company_name || order.username}</div>
                                        <small className="text-muted">ID: {order.user_id}</small>
                                    </td>
                                    <td><span className="fw-bold text-success">{Number(order.final_amount).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</span></td>
                                    <td>{new Date(order.created_at).toLocaleDateString('tr-TR')}</td>
                                    <td>{getStatusBadge(order.status)}</td>
                                    <td>
                                        <select 
                                            className="form-select form-select-sm" 
                                            value={order.status}
                                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                        >
                                            <option value="pending">{t('my_orders.status_pending')}</option>
                                            <option value="approved">{t('my_orders.status_approved')}</option>
                                            <option value="preparing">{t('my_orders.status_preparing')}</option>
                                            <option value="shipped">{t('my_orders.status_shipped')}</option>
                                            <option value="delivered">{t('my_orders.status_delivered')}</option>
                                            <option value="cancelled">{t('admin_orders.cancel')}</option>
                                        </select>
                                    </td>
                                    <td>
                                        <div className="d-flex gap-2">
                                            <Link to={`/orders/${order.id}`} className="btn btn-sm btn-info text-white" title={t('admin_orders.detail')}><FaEye /></Link>
                                            <button onClick={() => handleDelete(order.id)} className="btn btn-sm btn-outline-danger" title="Sil">
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredOrders.length === 0 && (
                                <tr><td colSpan="7" className="text-center p-5 text-muted">{t('admin_orders.not_found')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile: Card View */}
                <div className="d-lg-none bg-light p-3 rounded-3">
                    {filteredOrders.map(order => (
                        <div key={order.id} className="card border-0 shadow-sm mb-3 overflow-hidden">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center py-3 border-bottom-0">
                                <span className="fw-bold text-primary">#{order.order_number}</span>
                                <span className="badge bg-light text-muted fw-normal border">{new Date(order.created_at).toLocaleDateString('tr-TR')}</span>
                            </div>
                            <div className="card-body pt-0">
                                <div className="mb-3 pb-3 border-bottom border-dashed">
                                    <div className="small text-muted mb-1">{t('admin_orders.dealer_firm')}</div>
                                    <div className="fw-bold text-dark">{order.company_name || order.username}</div>
                                    <div className="small text-muted">ID: {order.user_id}</div>
                                </div>

                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <div className="p-2 rounded bg-light border border-light text-center h-100">
                                            <div className="small text-muted mb-1">{t('admin_orders.amount')}</div>
                                            <div className="fw-bold text-success">{Number(order.final_amount).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="p-2 rounded bg-light border border-light text-center h-100 d-flex flex-column align-items-center justify-content-center">
                                            <div className="small text-muted mb-1">{t('admin_orders.status')}</div>
                                            <div>{getStatusBadge(order.status)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-muted">{t('admin_orders.action')}</label>
                                    <select 
                                        className="form-select" 
                                        value={order.status}
                                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                    >
                                        <option value="pending">{t('my_orders.status_pending')}</option>
                                        <option value="approved">{t('my_orders.status_approved')}</option>
                                        <option value="preparing">{t('my_orders.status_preparing')}</option>
                                        <option value="shipped">{t('my_orders.status_shipped')}</option>
                                        <option value="delivered">{t('my_orders.status_delivered')}</option>
                                        <option value="cancelled">{t('admin_orders.cancel')}</option>
                                    </select>
                                </div>

                                <div className="d-flex gap-2">
                                    <Link to={`/orders/${order.id}`} className="btn btn-primary flex-grow-1">
                                        {t('admin_orders.detail')}
                                    </Link>
                                    <button onClick={() => handleDelete(order.id)} className="btn btn-outline-danger px-3">
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {filteredOrders.length === 0 && (
                        <div className="text-center p-4 text-muted">{t('admin_orders.not_found')}</div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default Orders;
